import { createAuthClient } from 'better-auth/react';
import { adminClient, organizationClient } from 'better-auth/client/plugins';
import { createAccessControl } from 'better-auth/plugins/access';
import type {
  AdminCompanyDTO,
  AdminChangeUserRoleDTO,
  AdminUpdateDemoExpirationDTO,
  AdminCreateCompanyDTO,
  AdminCreateUserDTO,
  AdminSendActivationEmailDTO,
  AvatarFinalizeUploadDTO,
  AvatarFinalizeUploadResponseDTO,
  AvatarPrepareUploadDTO,
  AvatarPrepareUploadResponseDTO,
  ChangePasswordDTO,
  UpdateProfileDTO,
} from '@shared/schemas';
import {
  ORG_PERMISSION_STATEMENTS,
  ORG_ROLE_PERMISSIONS,
  type OrganizationRole,
  type OrgPermissionAction,
  type OrgPermissionResource,
} from '@shared/schemas/organization';

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const authApiBaseUrl = `${apiBaseUrl}/api/auth`;

const organizationAccessControl = createAccessControl(ORG_PERMISSION_STATEMENTS);
const organizationRoles = {
  owner: organizationAccessControl.newRole(ORG_ROLE_PERMISSIONS.owner),
  admin: organizationAccessControl.newRole(ORG_ROLE_PERMISSIONS.admin),
  member: organizationAccessControl.newRole(ORG_ROLE_PERMISSIONS.member),
};

export const authClient = createAuthClient({
  baseURL: apiBaseUrl,
  fetchOptions: {
    credentials: 'include',
  },
  plugins: [
    adminClient(),
    organizationClient({
      ac: organizationAccessControl,
      roles: organizationRoles,
    }),
  ],
});

export function hasStaticOrganizationPermission<Resource extends OrgPermissionResource>(
  role: OrganizationRole,
  resource: Resource,
  action: OrgPermissionAction<Resource>,
) {
  const grantedActions = ORG_ROLE_PERMISSIONS[role][resource] as readonly string[];
  return grantedActions.includes(action);
}

export function getBrowserUrl(path: string) {
  return new URL(path, window.location.origin).toString();
}

type AuthRequestResult<T> = {
  data: T | null;
  error: unknown | null;
};

async function postAuthEndpoint<T>(path: string, body: Record<string, unknown>): Promise<AuthRequestResult<T>> {
  return postJsonEndpoint<T>(`${authApiBaseUrl}${path}`, body);
}

async function postApiEndpoint<T>(path: string, body: Record<string, unknown>): Promise<AuthRequestResult<T>> {
  return postJsonEndpoint<T>(`${apiBaseUrl}${path}`, body);
}

async function patchApiEndpoint<T>(path: string, body: Record<string, unknown>): Promise<AuthRequestResult<T>> {
  return postJsonEndpoint<T>(`${apiBaseUrl}${path}`, body, 'PATCH');
}

async function postJsonEndpoint<T>(
  url: string,
  body: Record<string, unknown>,
  method: 'POST' | 'PATCH' = 'POST',
): Promise<AuthRequestResult<T>> {
  const response = await fetch(url, {
    body: JSON.stringify(body),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method,
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    return {
      data: null,
      error: payload ?? { message: response.statusText, status: response.status },
    };
  }

  return {
    data: payload as T,
    error: null,
  };
}

async function getApiEndpoint<T>(
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): Promise<AuthRequestResult<T>> {
  return getJsonEndpoint<T>(`${apiBaseUrl}${path}`, query);
}

async function getJsonEndpoint<T>(
  url: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): Promise<AuthRequestResult<T>> {
  const searchParams = new URLSearchParams();

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      searchParams.set(key, String(value));
    });
  }

  const suffix = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
  const response = await fetch(`${url}${suffix}`, {
    credentials: 'include',
    method: 'GET',
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    return {
      data: null,
      error: payload ?? { message: response.statusText, status: response.status },
    };
  }

  return {
    data: payload as T,
    error: null,
  };
}

export function requestPasswordReset(email: string, redirectTo: string) {
  return postAuthEndpoint<{ message: string; status: boolean }>('/request-password-reset', {
    email,
    redirectTo,
  });
}

export function resetPassword(newPassword: string, token: string) {
  return postAuthEndpoint<{ status: boolean }>('/reset-password', {
    newPassword,
    token,
  });
}

export function sendVerificationEmail(email: string, callbackURL: string) {
  return postAuthEndpoint<{ status: boolean }>('/send-verification-email', {
    callbackURL,
    email,
  });
}

type ApiSuccessResponse<T> = {
  data: T;
  success: true;
};

export type AdminCompanyRecord = AdminCompanyDTO & {
  userCount?: number;
};

export type AdminUserQuotaListSnapshot = {
  extraTokens: number;
  renewsAt: string | null;
  tokenLimit: number;
  tokensConsumed: number;
};

export type AdminUserRecord = {
  accountType?: 'demo' | 'subscriber';
  banExpires?: string | null;
  banReason?: string | null;
  banned?: boolean;
  demoExpiresAt?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  /** Rôle dans l'entreprise de rattachement, distinct du rôle plateforme `role`. */
  companyRole?: OrganizationRole | null;
  createdAt: string;
  email: string;
  emailVerified: boolean;
  id: string;
  image?: string | null;
  name: string;
  quota?: AdminUserQuotaListSnapshot | null;
  role?: string | null;
  /** Cumul des tokens consommés par le membre, tous usages confondus. */
  tokensConsumed?: number;
  updatedAt: string;
};

export type ActiveSessionRecord = {
  createdAt: string;
  expiresAt: string;
  id: string;
  ipAddress?: string | null;
  token: string;
  updatedAt: string;
  userAgent?: string | null;
  userId: string;
};

export type ListUsersQuery = {
  accountType?: 'all' | 'demo' | 'subscriber';
  page?: number;
  pageSize?: number;
  role?: 'all' | 'admin' | 'user';
  search?: string;
  sortBy?: 'accountType' | 'createdAt' | 'email' | 'emailVerified' | 'name' | 'role' | 'status' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  status?: 'all' | 'active' | 'disabled';
  verification?: 'all' | 'pending' | 'verified';
};

type ListUsersResponse = {
  items: AdminUserRecord[];
  meta: {
    filteredTotal: number;
    page: number;
    pageCount: number;
    pageSize: number;
    total: number;
  };
  summary: {
    activeUsers: number;
    adminUsers: number;
    disabledUsers: number;
    pendingVerificationUsers: number;
    totalUsers: number;
    verifiedUsers: number;
  };
};

export function listUsers(query?: ListUsersQuery) {
  return getApiEndpoint<ListUsersResponse>('/api/admin/users', query);
}

export function listActiveSessions() {
  return getJsonEndpoint<ActiveSessionRecord[]>(`${authApiBaseUrl}/list-sessions`);
}

export function revokeSessionByToken(token: string) {
  return postAuthEndpoint<{ status: boolean }>('/revoke-session', { token });
}

export function revokeOtherSessions() {
  return postAuthEndpoint<{ status: boolean }>('/revoke-other-sessions', {});
}

export function banUser(userId: string, banReason?: string) {
  return postAuthEndpoint<{ user: AdminUserRecord }>('/admin/ban-user', {
    banReason,
    userId,
  });
}

export function unbanUser(userId: string) {
  return postAuthEndpoint<{ user: AdminUserRecord }>('/admin/unban-user', {
    userId,
  });
}

export function removeUser(userId: string) {
  return postAuthEndpoint<{ success: boolean }>('/admin/remove-user', {
    userId,
  });
}

export function createUserAsAdmin(payload: AdminCreateUserDTO) {
  return postApiEndpoint<ApiSuccessResponse<{
    companyId: string;
    companyName: string;
    userId: string;
  }>>('/api/admin/users', payload);
}

export function changeUserRole(payload: AdminChangeUserRoleDTO) {
  return postAuthEndpoint<{ user: AdminUserRecord }>('/admin/set-role', payload);
}

export function updateDemoExpiration(userId: string, payload: AdminUpdateDemoExpirationDTO) {
  return patchApiEndpoint<ApiSuccessResponse<{ demoExpiresAt: string | null; userId: string }>>(
    `/api/admin/users/${userId}/demo-expiration`,
    payload,
  );
}

export function sendActivationEmailAsAdmin(payload: AdminSendActivationEmailDTO) {
  return postApiEndpoint<ApiSuccessResponse<{ status: true }>>(
    '/api/admin/users/send-activation-email',
    payload,
  );
}

export function listCompanies() {
  return getApiEndpoint<ApiSuccessResponse<AdminCompanyRecord[]>>('/api/admin/companies');
}

export function createCompany(payload: AdminCreateCompanyDTO) {
  return postApiEndpoint<ApiSuccessResponse<AdminCompanyDTO>>('/api/admin/companies', payload);
}

export function setInitialPassword(newPassword: string) {
  return postAuthEndpoint<{ status: boolean }>('/set-password', {
    newPassword,
  });
}

export function updateCurrentUser(payload: UpdateProfileDTO) {
  return postAuthEndpoint<unknown>('/update-user', payload);
}

export function changeCurrentPassword(payload: ChangePasswordDTO) {
  return postAuthEndpoint<unknown>('/change-password', payload);
}

export function prepareAvatarUpload(payload: AvatarPrepareUploadDTO) {
  return postApiEndpoint<ApiSuccessResponse<AvatarPrepareUploadResponseDTO>>(
    '/api/account/avatar/prepare',
    payload,
  );
}

export function finalizeAvatarUpload(payload: AvatarFinalizeUploadDTO) {
  return postApiEndpoint<ApiSuccessResponse<AvatarFinalizeUploadResponseDTO>>(
    '/api/account/avatar/finalize',
    payload,
  );
}
